import { Button, Flex, Form, Steps } from "antd"
import type { FormInstance } from "antd/es/form/hooks/useForm"
import { type Transition } from "motion-dom"
import { AnimatePresence, motion } from "motion/react"
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react"

import type {
  DocumentAvailableBlock,
  DocumentTemplateMutateData
} from "../../types/types.ts"
import { TemplateFormFirstStep } from "./TemplateFormFirstStep/TemplateFormFirstStep.tsx"
import { TemplateFormSecondStep } from "./TemplateFormSecondStep.tsx"

export interface DocumentTemplateFormBodyHandle {
  goToStep: (step: number) => void
}

interface DocumentTemplateFormBodyProps {
  form: FormInstance<DocumentTemplateMutateData>
  availableBlocks: DocumentAvailableBlock[]
  loading?: boolean
}

const SLIDE_OFFSET = 40
const TRANSITION: Transition = { duration: 0.25, ease: "easeInOut" }

export const DocumentTemplateFormBody = forwardRef<
  DocumentTemplateFormBodyHandle,
  DocumentTemplateFormBodyProps
>(function DocumentTemplateFormBody(
  { form, availableBlocks, loading = false },
  ref
) {
  const [current, setCurrent] = useState(0)
  const prevStep = useRef(0)

  const totalSteps = 2

  const direction = current > prevStep.current ? 1 : -1

  const onChange = (value: number) => {
    prevStep.current = current
    setCurrent(value)
  }

  const handleNext = () => onChange(current + 1)
  const handlePrevious = () => onChange(current - 1)

  useImperativeHandle(ref, () => ({
    goToStep: (step: number) => onChange(step)
  }))

  const blocksLookup = useMemo(() => {
    return new Map<string, DocumentAvailableBlock>(
      availableBlocks?.map((b) => [b.block_type, b])
    )
  }, [availableBlocks])

  return (
    <Flex vertical gap={"middle"} className={"w-full h-full"}>
      <Steps
        current={current}
        onChange={onChange}
        items={[
          {
            disabled: loading,
            title: "Block Selection",
            description: "Choose blocks"
          },
          {
            disabled: loading,
            title: "Default Info",
            description: "Fill in block details"
          }
        ]}
      />

      {/* Step content */}
      <div className="flex-1 overflow-hidden relative">
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.div
            key={current}
            custom={direction}
            initial={{ opacity: 0, x: direction * SLIDE_OFFSET }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -SLIDE_OFFSET }}
            transition={TRANSITION}
            className="h-full overflow-auto"
          >
            <div className={current === 0 ? "block" : "hidden"}>
              <TemplateFormFirstStep form={form} blocksLookup={blocksLookup} />
            </div>
            <div className={current === 1 ? "block" : "hidden"}>
              <TemplateFormSecondStep
                disabled={loading}
                form={form}
                // blocksLookup={blocksLookup}
              />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation buttons */}
      <Flex gap={"small"} justify={"flex-end"}>
        <AnimatePresence mode="popLayout" initial={false}>
          {current > 0 && (
            <motion.div
              key="prev"
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={TRANSITION}
            >
              <Button disabled={loading} onClick={handlePrevious}>
                Previous
              </Button>
            </motion.div>
          )}

          {current < totalSteps - 1 ? (
            <motion.div
              key="next"
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 16 }}
              transition={TRANSITION}
            >
              <Button disabled={loading} type="primary" onClick={handleNext}>
                Next
              </Button>
            </motion.div>
          ) : (
            <motion.div
              key="submit"
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 16 }}
              transition={TRANSITION}
            >
              <Button loading={loading} type="primary" htmlType="submit">
                Save Template
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </Flex>

      <Form.Item<DocumentTemplateMutateData> name="blocks" hidden>
        <select />
      </Form.Item>
    </Flex>
  )
})
